import {COMPACT_TABLES_CLASS} from '@github-ui/copilot-chat/components/chat-message-table-class'
import {
  type ChatMessageContextProps,
  ChatMessageProvider,
  useChatMessage,
} from '@github-ui/copilot-chat/components/ChatMessageContext'
import {Confirmation} from '@github-ui/copilot-chat/components/Confirmation'
import {CopilotBadge} from '@github-ui/copilot-chat/components/CopilotBadgeV2'
import {AgentErrors, ErrorMessage} from '@github-ui/copilot-chat/components/Errors'
import {Feedback} from '@github-ui/copilot-chat/components/Feedback'
import {messageFromFunctionCall} from '@github-ui/copilot-chat/components/FunctionLoadingUtils'
import {InterruptedBanner} from '@github-ui/copilot-chat/components/InterruptedBanner'
import {MessageTokenUsage} from '@github-ui/copilot-chat/components/MessageTokenUsage'
import {RetryButton} from '@github-ui/copilot-chat/components/RetryButton'
import {Toolbar} from '@github-ui/copilot-chat/components/Toolbar'
import {UserMessage} from '@github-ui/copilot-chat/components/UserMessage'
import {useChatStateLens, useChatStateValues} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useChatMessageBehavior} from '@github-ui/copilot-chat/hooks/use-chat-message-behavior'
import {useNavigateToNewThread} from '@github-ui/copilot-chat/hooks/use-navigate-to-new-thread'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import {MAX_SUBTHREADS_PER_MESSAGE} from '@github-ui/copilot-chat/utils/copilot-chat-manager'
import {buildContentChunks, deriveToolGroupStatus} from '@github-ui/copilot-chat/utils/tool-grouping'
import {useGetChatState} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {AUTO_MODEL_ID, getAutoModel} from '@github-ui/copilot-chat-helpers/auto-model'
import {filterUniqueConfirmations, srOnlyHeader} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {getParentMessage} from '@github-ui/copilot-chat-helpers/copilot-chat-subthreading-helpers'
import {type CopilotChatMessage, type Invocation, NullMessageId} from '@github-ui/copilot-chat-types'
import {WithShimmerEffect} from '@github-ui/copilot-chat-visuals/WithShimmerEffect'
import {useFetchCopilotSpace} from '@github-ui/copilot-spaces/hooks'
import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {testIdProps} from '@github-ui/test-id-props'
import {CommandIconButton} from '@github-ui/ui-commands'
import {PencilIcon} from '@primer/octicons-react'
import {Banner, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {memo, type ReactNode, useCallback, useEffect, useEffectEvent, useMemo, useRef} from 'react'

import {useIsSharedThread} from '../hooks/use-is-shared-thread'
import {useOnGitHubFileLinkClick} from '../hooks/use-on-github-file-link-click'
import {ChatImageAttachment} from './ChatImageAttachment'
import styles from './ChatMessage.module.css'
import {ChatMessageReferences} from './ChatMessageReferences'
import {ChatPagingComponent} from './ChatPagingComponent'
import {ClientConfirmations} from './ClientConfirmations'
import type {PreviewableContentIdentifier} from './ContentPreview/content-preview-types'
import {stripVersionFromId} from './ContentPreview/content-preview-types'
import {useContentPreviewActions} from './ContentPreview/ContentPreviewContext'
import {MarkdownViewer, MemoizedMarkdownViewer} from './MarkdownViewer'
import {spaceConversationVariants, useSpaceConversationStatus} from './Spaces/SpaceConversationBanner'
import {TimelineEvents, userMessageIsTimelineEvent} from './timeline-events/TimelineEvents'
import {ToolCallLogs} from './ToolCallLogs/ToolCallLogs'
import {ToolGroup} from './ToolGroup/ToolGroup'
import UserMessageEdit from './UserMessageEdit'

export interface ChatMessageProps
  extends ChatMessageContextProps, Omit<InnerChatMessageProps, 'isLatestUserMessage' | 'parentMessage'> {}

type InnerChatMessageProps = {
  isLatestMessage?: boolean
  isSharedMessage?: boolean
  isFirstReply?: boolean
  inputRef?: React.RefObject<HTMLInputElement | null> | React.RefObject<HTMLTextAreaElement | null>
  isStreaming?: boolean
  readOnly?: boolean
  excludeFeedback?: boolean
  messageIndex: number
  autoOpenPreviewPane: boolean
  isLatestUserMessage: boolean
  parentMessage: CopilotChatMessage | undefined
  /** Used in testing, this function is called every time the component renders */
  reportRender?: (messageId: string) => void
}

const InnerChatMessage = ({
  excludeFeedback,
  inputRef,
  isLatestMessage,
  isLatestUserMessage,
  isFirstReply,
  isStreaming,
  readOnly = false,
  parentMessage,
  messageIndex,
  autoOpenPreviewPane,
  ...props
}: InnerChatMessageProps) => {
  const {message} = useChatMessage()
  const manager = useChatManager()

  // DO NOT ATTEMPT TO USE `messages` IN THIS COMPONENT YOU'LL MAKE IT RE-RENDER TOO MUCH
  const state = useChatStateValues('mode', 'wrapCodeLines')
  // INSTEAD, ACCESS `messages` IN THE `ChatMessageWrapped` COMPONENT AND PASS THE DERIVED VALUE TO `InnerChatMessage` AS A PROP

  const getChatState = useGetChatState()
  const {
    getItems: getContentPreviewItems,
    getVersionedItems: getContentPreviewVersionedItems,
    openItem: openContentPreviewItem,
    removeItems: removeContentPreviewItems,
    getOpenItems: getOpenContentPreviewItems,
    getOpenItemsBeforeSubthreadChange,
    setOpenItemsBeforeSubthreadChange,
    enableLoadingState: enableContentPreviewLoadingState,
    disableLoadingState: disableContentPreviewLoadingState,
  } = useContentPreviewActions()
  const removeOutdatedContentPreviewItems = useCallback((): void => {
    const localChatState = getChatState()
    const currentItems = getContentPreviewItems()
    // calculate the outdated messages we want to clear
    const messageIdsToClear = []
    let currentMessage: CopilotChatMessage | undefined = message
    while (currentMessage != null) {
      messageIdsToClear.push(currentMessage.id)
      if (currentMessage.selectedChildIndex !== undefined) {
        currentMessage = localChatState.messages[currentMessage.selectedChildIndex]
      } else {
        currentMessage = undefined
      }
    }

    // File Browser uses file ids rather than message ids
    // so create a map of message ids to file ids to make the next step easier
    const messageIdsToFiles = new Map<string, PreviewableContentIdentifier[]>()
    for (const [k, v] of currentItems) {
      const files = messageIdsToFiles.get(v.messageId) || []
      files.push(k)
      messageIdsToFiles.set(v.messageId, files)
    }

    // using the outdated message ids, get the outdated file ids
    const fileIdsToClear: PreviewableContentIdentifier[] = []
    for (const messageId of messageIdsToClear) {
      const fileKeys = messageIdsToFiles.get(messageId)
      if (fileKeys) {
        for (const fileKey of fileKeys) {
          fileIdsToClear.push(fileKey)
        }
      }
    }

    // clear files with no message id. These may have been opened from clicking an input reference.
    for (const [id, previewItem] of currentItems) {
      if (previewItem.messageId === NullMessageId) {
        fileIdsToClear.push(id)
      }
    }

    const openContentPreviewItems = getOpenContentPreviewItems()
    setOpenItemsBeforeSubthreadChange([...openContentPreviewItems])

    // enable loading state if visible files will be cleared
    if (fileIdsToClear.some(fileIdToClear => openContentPreviewItems.includes(fileIdToClear))) {
      enableContentPreviewLoadingState()
    }

    removeContentPreviewItems(fileIdsToClear)
  }, [
    getContentPreviewItems,
    enableContentPreviewLoadingState,
    getChatState,
    message,
    getOpenContentPreviewItems,
    removeContentPreviewItems,
    setOpenItemsBeforeSubthreadChange,
  ])

  const {
    author,
    handleConfirmationAction,
    handleRetryErrorMessage,
    handleRetryCopilotResponse,
    handleEditUserMessage,
    hasClientConfirmations,
    isAgentError,
    isAI,
    isCopilot,
    isError,
    isInterrupted,
    isUser,
    myRef,
    rendererConfig,
    renderFeedback,
    shouldShowMessageActions,
    skillExecutionToUseForRespondingText,
    showRetryButton,
  } = useChatMessageBehavior({
    excludeFeedback,
    inputRef,
    isLatestMessage,
    isStreaming,
    removeOutdatedContentPreviewItems,
  })

  const onGitHubLinkClick = useOnGitHubFileLinkClick()
  const isViewingSharedThread = useIsSharedThread()
  const editing = useChatStateLens(s => !readOnly && s.editingMessage === message.id)

  const shouldShowMessageActionsPlaceholder =
    isStreaming &&
    !isError &&
    !message.confirmations &&
    !shouldShowMessageActions &&
    isLatestMessage &&
    (!excludeFeedback || isAI)

  const currentSubthread =
    (parentMessage?.childMessageIndexes?.findIndex(messageIdx => messageIdx === messageIndex) || 0) + 1 || 1
  const totalSubthreads = parentMessage?.childMessageIndexes?.length || 1
  const maxSubthreadsReached = totalSubthreads >= MAX_SUBTHREADS_PER_MESSAGE
  const maxMessagesReached = manager.maxMessagesReached()
  const hasSubthreads = totalSubthreads > 1

  const images = useMemo(
    () => message.mediaContent?.filter(media => media.mediaType.startsWith('image/')),
    [message.mediaContent],
  )
  const shouldShowImages = useMemo(() => Boolean(images?.length), [images])

  const shouldShowSharedMessageActions =
    shouldShowMessageActions && isViewingSharedThread && (isCopilot || hasSubthreads)
  const hasReadOnlyMessageActions =
    (isCopilot && !isStreaming && (!!message.content || !!message.usage)) || hasSubthreads

  const previousMessageIdRef = useRef<string | null>(null)
  const openLatestVersion = useEffectEvent((id: PreviewableContentIdentifier) => {
    const latestVersion = getContentPreviewVersionedItems().get(stripVersionFromId(id))?.at(-1)
    if (latestVersion) openContentPreviewItem(latestVersion, false)
  })

  // Clear the content preview if we get a `publicCode` error back.
  useEffect(() => {
    if (message.error?.type !== 'publicCode') return

    const currentItems = getContentPreviewItems()
    const itemsToRemove: PreviewableContentIdentifier[] = []
    for (const [id, previewItem] of currentItems) {
      if (previewItem.messageId === message.id || previewItem.messageId === previousMessageIdRef.current) {
        itemsToRemove.push(id)
      }
    }

    if (itemsToRemove.length > 0) {
      removeContentPreviewItems(itemsToRemove)
    }
  }, [message.error?.type, message.id, getContentPreviewItems, removeContentPreviewItems])

  useEffect(() => {
    // When changing subthreads, we remove all the unmounted files as you'd expect. But if a file was open before
    // changing the subthread, we want to reopen the latest version after, so users can smoothly
    // navigate across. This is complicated by the fact that the files are only obtained after rendering the `FileBlock`.
    // So, after we render the last message, we want to open the latest versions. But this effect will run before
    // ContentPreviewContext updates the `contentPreviewVersionedItems`, so we have to delay with a timeout.

    // Messages are keyed by index, so the same component instance can render two separate messages when switching
    // subthreads, so we have to see if `message.id` changed rather than just running this on mount.
    const itemsBeforeChange = getOpenItemsBeforeSubthreadChange()
    if (previousMessageIdRef.current !== message.id && isLatestMessage && itemsBeforeChange.length > 0) {
      const timeout = setTimeout(() => {
        for (const openItem of itemsBeforeChange) openLatestVersion(openItem)
        disableContentPreviewLoadingState()
        setOpenItemsBeforeSubthreadChange([])
      })
      return () => clearTimeout(timeout)
    }
    previousMessageIdRef.current = message.id
  }, [
    disableContentPreviewLoadingState,
    message.id,
    getOpenItemsBeforeSubthreadChange,
    isLatestMessage,
    setOpenItemsBeforeSubthreadChange,
  ])

  const handlePrevSubthreadClick = useCallback(() => {
    const {messages} = getChatState()
    const currentIndex = currentSubthread - 1
    const messageIndexToSelect = parentMessage?.childMessageIndexes?.[currentIndex - 1]
    if (messageIndexToSelect == null) return
    const messageToSelect = messages[messageIndexToSelect]
    if (messageToSelect == null) return
    manager.setSelectedMessage(messageToSelect)

    removeOutdatedContentPreviewItems()
  }, [currentSubthread, manager, parentMessage, removeOutdatedContentPreviewItems, getChatState])

  const handleNextSubthreadClick = useCallback(() => {
    const {messages} = getChatState()
    const currentIndex = currentSubthread - 1
    const messageIndexToSelect = parentMessage?.childMessageIndexes?.[currentIndex + 1]
    if (messageIndexToSelect == null) return
    const messageToSelect = messages[messageIndexToSelect]
    if (messageToSelect == null) return
    manager.setSelectedMessage(messageToSelect)

    removeOutdatedContentPreviewItems()
  }, [currentSubthread, manager, parentMessage, removeOutdatedContentPreviewItems, getChatState])

  const navigateToNewThread = useNavigateToNewThread()

  const {model: threadModel, availableModels} = useChatStateValues('model', 'availableModels')
  let messageModel = threadModel
  if (message.model) {
    const models = availableModels ?? [messageModel]
    messageModel = models.find(m => m.id === message.model) || messageModel
  }

  // For an Auto-generated message, reveal the concrete routed model in the retry
  // label. Resolve message.model's display name from the selectable list, but fall
  // back to the raw id (mirroring the Auto chip) rather than the Auto thread model,
  // so Free/EDU users — whose routed model isn't in their selectable list — don't
  // see the degenerate "Auto (Auto)".
  const autoParenthetical =
    message.model && message.model !== AUTO_MODEL_ID
      ? (availableModels?.find(m => m.id === message.model)?.displayName ?? message.model)
      : undefined
  const retryModel = message.generatedWithAuto
    ? {...getAutoModel(), displayName: autoParenthetical ? `Auto (${autoParenthetical})` : 'Auto'}
    : messageModel

  const startEditing = useCallback(() => manager.startEditingMessage(message.id), [manager, message.id])

  const customCopilotId = useSelectedCopilotSpaceId()
  const {data: customCopilot} = useFetchCopilotSpace(customCopilotId)
  const customCopilotExists = Boolean(customCopilot)
  const customCopilotDisabled = !!customCopilotId && !customCopilotExists

  const {selectedThreadID} = useChatStateValues('selectedThreadID')
  const {variant: spaceViewVariant} = useSpaceConversationStatus(customCopilotId, selectedThreadID)
  const spaceSizeLimitExceeded = spaceViewVariant === spaceConversationVariants.customCopilotSizeExceeded

  const subthreadingDisabled =
    maxSubthreadsReached || maxMessagesReached || customCopilotDisabled || spaceSizeLimitExceeded

  const waitingForCopilotContent = isCopilot && isStreaming && isLatestMessage && !message.content

  const contentChunkLayout = useMemo(
    () =>
      message.contentParts && message.contentParts.length > 0 ? buildContentChunks(message.contentParts) : undefined,
    [message.contentParts],
  )

  // When the latest rendered chunk is a tool group that's still running, that row already
  // shimmers the active skill, so suppress the trailing working indicator to avoid narrating
  // the same skill twice. The indicator still shows in the gap between/after tool calls.
  const latestChunk = contentChunkLayout?.chunks.at(-1)
  const latestToolCallRunning =
    latestChunk?.kind === 'toolGroup' && deriveToolGroupStatus(latestChunk.skills) === 'running'

  const onWrapCodeLinesChange = useCallback(
    (wrap: boolean) => {
      manager.setWrapCodeLines(wrap)
    },
    [manager],
  )

  // Activate a clickable suggestion: a normal click submits it; Alt/Option pre-fills the composer without sending.
  const onSuggestionSelect = useCallback(
    (text: string, {submit}: {submit: boolean}) =>
      submit ? manager.submitChatInputText(text) : manager.setChatInputText(text),
    [manager],
  )

  // while we're streaming we update the markdown viewer a lot and there's no need to memoize
  const MarkdownComponent = isStreaming ? MarkdownViewer : MemoizedMarkdownViewer

  if ((process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') && props.reportRender) {
    props.reportRender(message.id)
  }

  const isAssistive = state.mode === 'assistive'
  return (
    <div
      className={clsx(
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        'message-container',
        styles.chatMessage,
        isUser && styles.user,
        isAI && styles.ai,
        editing ? styles.editing : '',
        isLatestMessage && styles.latest,
      )}
      ref={myRef}
      data-center-avatar={(message.skillExecutions?.length ?? 0) > 0 ? '' : undefined}
      {...testIdProps(`message-${isError ? 'error' : isStreaming ? 'streaming' : message.id}`)}
    >
      {isAI ? (
        <CopilotBadge
          isLoading={isStreaming && isLatestMessage}
          isThinking={isStreaming && message.content === '' && isLatestMessage}
          isError={isError && message.error?.type !== 'agentUnauthorized'}
          className={styles.avatar}
          mode={state.mode}
          isFirstMessage={isFirstReply}
          message={message.content || ''}
          createdAt={message.createdAt}
        />
      ) : null}
      {isUser && shouldShowImages && (
        <div className={images?.length === 4 ? styles.imageGrid : styles.imageRow}>
          {images?.map(image => (
            <ChatImageAttachment
              name={image?.name || 'Uploaded image'}
              messageId={message.id}
              src={image?.url || undefined}
              key={image?.url || ''}
              alt={`Uploaded image${image?.name ? `: ${image.name}` : ''}`}
              mode={state.mode}
              squareView={images?.length >= 2}
            />
          ))}
        </div>
      )}
      {isUser && !hasClientConfirmations && message.references && message.references.length > 0 && (
        <ChatMessageReferences
          className={styles.references}
          messageId={message.id}
          messageTimestamp={message.createdAt}
          messageIndex={messageIndex}
          references={message.references}
          size="medium"
        />
      )}
      {!editing && (
        <div className={clsx(styles.content, isAssistive && styles.assistive)}>
          {
            <>
              <div className={clsx('js-snippet-clipboard-copy-unpositioned', styles.messageArea)}>
                {hasClientConfirmations ? (
                  <ClientConfirmations
                    authorName={author.name}
                    clientConfirmations={message.clientConfirmations ?? []}
                  />
                ) : isUser ? (
                  <UserMessage
                    accessibleHeader={srOnlyHeader('You', message.content ?? '')}
                    className={styles.userMessage}
                  />
                ) : (
                  <>
                    {contentChunkLayout ? (
                      <>
                        {(() => {
                          const {chunks, firstTextIndex, lastTextIndex} = contentChunkLayout
                          return (
                            <>
                              {firstTextIndex === -1 && <h3 className="sr-only">{srOnlyHeader('Copilot', '')}</h3>}
                              {chunks.map(chunk => {
                                if (chunk.kind === 'text') {
                                  return (
                                    <MessageErrorBoundary key={`text-${chunk.partIndex}`}>
                                      <MarkdownComponent
                                        autoOpenPreviewPane={autoOpenPreviewPane}
                                        markdown={chunk.content}
                                        messageId={message.id}
                                        messageIndex={messageIndex}
                                        messageTimestamp={message.createdAt}
                                        accessibleHeader={
                                          chunk.partIndex === firstTextIndex
                                            ? srOnlyHeader('Copilot', chunk.content)
                                            : undefined
                                        }
                                        copilotAnnotations={
                                          chunk.partIndex === lastTextIndex ? message.copilotAnnotations : undefined
                                        }
                                        references={message.references ?? undefined}
                                        wrapCodeLines={state.wrapCodeLines}
                                        onWrapCodeLinesChange={onWrapCodeLinesChange}
                                        onGitHubLinkClick={onGitHubLinkClick}
                                        onSuggestionSelect={readOnly ? undefined : onSuggestionSelect}
                                        chatMode={state.mode}
                                        {...rendererConfig}
                                        className={COMPACT_TABLES_CLASS}
                                      />
                                    </MessageErrorBoundary>
                                  )
                                }
                                return (
                                  <div key={`toolgroup-${chunk.partIndex}`} className={styles.toolGroupChunk}>
                                    <ToolGroup skillExecutions={chunk.skills} />
                                  </div>
                                )
                              })}
                            </>
                          )
                        })()}
                        {waitingForCopilotContent && !latestToolCallRunning && (
                          <WithShimmerEffect className={clsx(styles.skillExecutionText, 'mb-2')}>
                            {`${messageFromFunctionCall(skillExecutionToUseForRespondingText)}...`}
                          </WithShimmerEffect>
                        )}
                      </>
                    ) : (
                      <>
                        <ToolCallLogs
                          waitingForCopilotContent={waitingForCopilotContent}
                          currentSkillExecution={skillExecutionToUseForRespondingText}
                          isAssistive={isAssistive}
                        />
                        {message.content && (
                          // There is a boundary around the whole message which is useful for catching message logic errors,
                          // but if an error occurs while rendering markdown then we still want the user to be able to copy
                          // that markdown / retry / etc. So an identical boundary around just the renderer is useful.
                          <MessageErrorBoundary>
                            <MarkdownComponent
                              autoOpenPreviewPane={autoOpenPreviewPane}
                              markdown={message.content}
                              messageId={message.id}
                              messageIndex={messageIndex}
                              messageTimestamp={message.createdAt}
                              accessibleHeader={srOnlyHeader('Copilot', message.content ?? '')}
                              copilotAnnotations={message.copilotAnnotations}
                              references={message.references ?? undefined}
                              wrapCodeLines={state.wrapCodeLines}
                              onWrapCodeLinesChange={onWrapCodeLinesChange}
                              onGitHubLinkClick={onGitHubLinkClick}
                              onSuggestionSelect={readOnly ? undefined : onSuggestionSelect}
                              chatMode={state.mode}
                              {...rendererConfig}
                              className={COMPACT_TABLES_CLASS}
                            />
                          </MessageErrorBoundary>
                        )}
                      </>
                    )}
                    {isError ? <ErrorMessage manager={manager} /> : null}
                    {isAgentError && message.agentErrors && <AgentErrors errors={message.agentErrors} />}
                    {isInterrupted ? (
                      <InterruptedBanner messageHasContent={!!message.content || !!message.skillExecutions?.length} />
                    ) : null}
                    {isAI &&
                      !waitingForCopilotContent &&
                      filterUniqueConfirmations(message.confirmations).map((confirmation, i) => {
                        const name = (confirmation.confirmation as Invocation).name
                        switch (name) {
                          case 'indexrepo':
                            return null
                          default:
                            return (
                              <Confirmation
                                confirmation={confirmation}
                                handleConfirmation={handleConfirmationAction}
                                // eslint-disable-next-line @eslint-react/no-array-index-key
                                key={i}
                                isLatestMessage={isLatestMessage}
                                readOnly={readOnly}
                              />
                            )
                        }
                      })}
                  </>
                )}
                {hasSubthreads && !isLatestMessage && (
                  <div className={styles.messageSubthreadIndicator} data-testid="chat-paging-indicator">
                    {currentSubthread}/{totalSubthreads}
                  </div>
                )}
              </div>
              {shouldShowMessageActionsPlaceholder && <div className={styles.actionsPlaceholder} />}
              {!readOnly && !shouldShowMessageActions && showRetryButton && (
                <RetryButton handleRetryMessage={handleRetryErrorMessage} disabled={subthreadingDisabled} />
              )}
              {shouldShowMessageActions &&
                !isViewingSharedThread &&
                (!readOnly || hasReadOnlyMessageActions) &&
                !(isUser && hasClientConfirmations && !hasSubthreads) && (
                  <Toolbar aria-label="Message tools" className={styles.actions} data-testid="nonshared-toolbar">
                    {!readOnly &&
                      isUser &&
                      !hasClientConfirmations &&
                      (isLatestUserMessage ? (
                        <CommandIconButton
                          // This will show the keybinding hint, but we still have to bind the onClick handler because
                          // this button is outside the command scope (which is only the input - pressing the up arrow
                          // only works when the input is focused)
                          commandId="copilot-chat:edit-last-message"
                          variant="invisible"
                          aria-label="Edit message"
                          onClick={startEditing}
                          icon={PencilIcon}
                          disabled={subthreadingDisabled}
                          {...testIdProps('edit-message-button')}
                        />
                      ) : (
                        <IconButton
                          variant="invisible"
                          aria-label="Edit message"
                          onClick={startEditing}
                          icon={PencilIcon}
                          disabled={subthreadingDisabled}
                          {...testIdProps('edit-message-button')}
                        />
                      ))}
                    {isCopilot && (
                      <>
                        {!readOnly && renderFeedback && <Feedback iconSize="medium" />}
                        {!!message.content && !isStreaming && (
                          <CopyToClipboardIconButton
                            textToCopy={message.content ?? ''}
                            ariaLabel="Copy to clipboard"
                            size="medium"
                            className="d-flex flex-items-center"
                            onCopy={() =>
                              sendEvent('dotcom_chat.activate', {target: 'RESPONSE_ACTION_COPY', mode: 'immersive'})
                            }
                          />
                        )}
                      </>
                    )}
                    {isCopilot && !!message.usage && !isStreaming && (
                      <MessageTokenUsage usage={message.usage} modelId={message.model} />
                    )}
                    {!readOnly &&
                      (showRetryButton && isError ? (
                        <RetryButton handleRetryMessage={handleRetryErrorMessage} disabled={subthreadingDisabled} />
                      ) : (
                        isAI &&
                        !isError &&
                        !message.clientSide && (
                          <RetryButton
                            handleRetryMessage={handleRetryCopilotResponse}
                            disabled={subthreadingDisabled}
                            showModelPicker={!!availableModels && availableModels.length > 1}
                            model={retryModel}
                            navigateToNewThread={navigateToNewThread}
                          />
                        )
                      ))}
                    {hasSubthreads && (
                      <ChatPagingComponent
                        currentPage={currentSubthread}
                        totalPages={totalSubthreads}
                        onPrev={handlePrevSubthreadClick}
                        onNext={handleNextSubthreadClick}
                        isUserMessage={isUser}
                      />
                    )}
                  </Toolbar>
                )}

              {/* When viewing a shared thread conversation, show copy to clipboard for Copilot responses and show
                  subthread navigation if a message has subthreads. Edit, feedback, retry should never show up. */}
              {shouldShowSharedMessageActions && (
                <Toolbar aria-label="Message tools" className={styles.sharedThreadActions} data-testid="shared-toolbar">
                  {isCopilot && (
                    <>
                      {!!message.content && !isStreaming && (
                        <CopyToClipboardIconButton
                          textToCopy={message.content ?? ''}
                          ariaLabel="Copy to clipboard"
                          size="medium"
                          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                          className="d-flex flex-items-center actions"
                          onCopy={() =>
                            sendEvent('dotcom_chat.activate', {target: 'RESPONSE_ACTION_COPY', mode: 'immersive'})
                          }
                        />
                      )}
                    </>
                  )}
                  {hasSubthreads && (
                    <ChatPagingComponent
                      currentPage={currentSubthread}
                      totalPages={totalSubthreads}
                      onPrev={handlePrevSubthreadClick}
                      onNext={handleNextSubthreadClick}
                      isUserMessage={isUser}
                    />
                  )}
                </Toolbar>
              )}
            </>
          }
        </div>
      )}
      {editing && (
        <UserMessageEdit
          messageContent={message.content || ''}
          onSubmit={handleEditUserMessage}
          onCancel={() => manager.cancelEditingMessage()}
        />
      )}
    </div>
  )
}

const MessageErrorBoundary = ({children}: {children: ReactNode}) => (
  <ErrorBoundary
    boundaryName="copilot-chat-message"
    fallback={
      <Banner
        className={styles.errorFallback}
        variant="warning"
        title="Message cannot be displayed"
        description="An unknown error occurred while attempting to display this message."
      />
    }
  >
    {children}
  </ErrorBoundary>
)

const ChatMessageUnmemoized = ({
  message,
  messageIndex,
  isSharedMessage,
  isLatestMessage,
  ...props
}: ChatMessageContextProps & InnerChatMessageProps) => {
  const isViewingSharedThread = useIsSharedThread()
  return (
    <ChatMessageProvider message={message}>
      {!userMessageIsTimelineEvent(message) && (
        <InnerChatMessage
          {...props}
          messageIndex={messageIndex}
          isSharedMessage={isSharedMessage}
          isLatestMessage={isLatestMessage}
        />
      )}
      <TimelineEvents
        message={message}
        messageIndex={messageIndex}
        isViewingSharedThread={isViewingSharedThread}
        isSharedMessage={isSharedMessage}
        isLatestMessage={isLatestMessage}
      />
    </ChatMessageProvider>
  )
}

const ChatMessageMemoized = memo(ChatMessageUnmemoized)

const ChatMessageWrapped = (props: ChatMessageProps) => {
  // this component exists to reference `messages` in chat state. we don't do this in the memoized component because
  // `messages` is changing all the time, but rarely in a way that requires a re-render of the message.
  const {message} = props
  const parentMessage = useChatStateLens(s => getParentMessage(s.messages, message))
  const isLatestUserMessage = useChatStateLens(
    s =>
      message.role === 'user' &&
      // Match the editLastMessage shortcut: only the latest *editable* user
      // message (i.e. not a tool confirmation reply) is the keybinding target.
      s.messages.findLast(m => m.role === 'user' && !(m.clientConfirmations && m.clientConfirmations.length > 0))
        ?.id === message.id,
  )

  return <ChatMessageMemoized {...props} parentMessage={parentMessage} isLatestUserMessage={isLatestUserMessage} />
}

export const ChatMessage: React.FunctionComponent<ChatMessageProps> = (props: ChatMessageProps) => {
  // this component exists to wrap the ChatMessage stuff in an error boundary
  return (
    <MessageErrorBoundary>
      <ChatMessageWrapped {...props} />
    </MessageErrorBoundary>
  )
}
